import React, { useEffect, useState } from "react";
import { api, fmtBRL, PLAN_LABELS, STATUS_CLASS, STATUS_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldStar, CheckCircle, ArrowRight, ArrowLeft, MagnifyingGlass, PenNib, Warning, CaretRight, FileText, Copy, Link as LinkIcon, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { InvoicePayment } from "@/components/InvoicePayment";

// STEPS: 1 client + credit  |  2 property + plan  |  3 review + sign
const STEPS = [
  { n: 1, label: "Cliente & Análise" },
  { n: 2, label: "Imóvel & Plano" },
  { n: 3, label: "Contrato & Assinatura" },
];

const PLAN_INFO = {
  basico: { name: "Básico", mult: 30, rate: 0.09, features: ["Débitos de aluguéis e condomínios", "Taxas e IPTU", "Pintura interna", "Multa rescisória"] },
  plus:   { name: "Fit",    mult: 40, rate: 0.11, features: ["Tudo do plano Básico", "Danos ao imóvel", "Mais segurança"] },
  ultra:  { name: "Ultra",  mult: 50, rate: 0.15, features: ["Todos os planos em 1", "Cobertura ampliada", "E muito mais"] },
};

export default function GarantiaWizard() {
  const [step, setStep] = useState(1);
  // Step 1
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [rentInput, setRentInput] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  // Step 2
  const [property, setProperty] = useState({ address: "", rent: "", condo: "", iptu: "", owner_name: "" });
  const [plan, setPlan] = useState("plus");
  // Step 3
  const [signature, setSignature] = useState("");
  const [contract, setContract] = useState(null);
  const [signing, setSigning] = useState(false);
  const [activation, setActivation] = useState(null);

  useEffect(() => {
    api.get(`/clients${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setClients(r.data));
  }, [q]);

  const runAnalysis = async () => {
    if (!selected || !rentInput) { toast.error("Selecione cliente e informe o aluguel"); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/credit/analyze", { client_id: selected.client_id, rent_amount: parseFloat(rentInput) });
      setAnalysis(data);
      if (data.status === "reprovado") toast.error("Análise reprovada — cliente não elegível");
      else toast.success(`Aprovado — score ${data.score}`);
    } catch { toast.error("Erro na análise"); }
    finally { setAnalyzing(false); }
  };

  const goStep2 = () => {
    setProperty({ ...property, rent: rentInput });
    setStep(2);
  };

  const goStep3 = async () => {
    if (!property.address || !property.rent) { toast.error("Preencha endereço e aluguel"); return; }
    try {
      const { data } = await api.post("/contracts", {
        client_id: selected.client_id,
        property_address: property.address,
        rent_amount: parseFloat(property.rent),
        condo_amount: parseFloat(property.condo || 0),
        iptu_amount: parseFloat(property.iptu || 0),
        plan,
        duration_months: 30,
        owner_name: property.owner_name || "Proprietário",
        credit_score: analysis?.score,
      });
      setContract(data);
      setStep(3);
    } catch { toast.error("Erro ao gerar contrato"); }
  };

  const sign = async () => {
    if (!signature.trim()) { toast.error("Digite seu nome para assinar"); return; }
    setSigning(true);
    try {
      const { data } = await api.post(`/contracts/${contract.contract_id}/sign`, { signature });
      setContract(data.contract);
      setActivation(data.activation_invoice);
      toast.success("Contrato assinado e ativado! Boleto de ativação gerado.");
    } catch { toast.error("Erro na assinatura"); }
    finally { setSigning(false); }
  };

  const restart = () => {
    setStep(1); setSelected(null); setRentInput(""); setAnalysis(null);
    setProperty({ address: "", rent: "", condo: "", iptu: "", owner_name: "" });
    setPlan("plus"); setSignature(""); setContract(null); setActivation(null);
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-6">
        <div className="eyebrow mb-1">Nova Contratação</div>
        <h1 className="font-display text-4xl tracking-tight">Garantia Locatícia</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-full border ${step >= s.n ? "bg-[var(--emerald)] text-white border-transparent" : "bg-white text-slate-500 border-[var(--line)]"}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step > s.n ? "bg-white/20" : step === s.n ? "bg-white/20" : "bg-slate-100"}`}>
                {step > s.n ? <CheckCircle size={14} weight="fill" /> : s.n}
              </div>
              <span className="text-xs font-medium">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <CaretRight size={14} color="#94a3b8" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="card-flat p-6 lg:col-span-3">
            <div className="eyebrow mb-3">Selecione o cliente inquilino</div>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="wiz-client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou CPF" className="pl-10 mb-3" />
            </div>
            <div className="max-h-72 overflow-y-auto border border-[var(--line)] rounded" data-testid="wiz-client-list">
              {clients.length === 0 && <div className="p-4 text-sm text-slate-400">Cadastre clientes primeiro em "Clientes".</div>}
              {clients.map((c) => (
                <button key={c.client_id} onClick={() => setSelected(c)} data-testid={`wiz-client-${c.client_id}`}
                  className={`w-full text-left p-3 border-b border-[var(--line)] last:border-0 hover:bg-slate-50 transition-colors ${selected?.client_id === c.client_id ? "bg-[var(--emerald-soft)]" : ""}`}>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-slate-500 mono">{c.cpf} · {c.phone}</div>
                </button>
              ))}
            </div>

            <div className="mt-6">
              <Label className="text-xs text-slate-500 mb-1.5 block">Valor do aluguel (para análise)</Label>
              <Input data-testid="wiz-rent" type="number" value={rentInput} onChange={(e) => setRentInput(e.target.value)} placeholder="2500" className="max-w-xs" />
            </div>
            <Button data-testid="wiz-run-analysis" onClick={runAnalysis} disabled={!selected || !rentInput || analyzing} className="btn-primary mt-4 h-11">
              <ShieldStar size={16} className="mr-2" /> {analyzing ? "Consultando…" : "Rodar Análise de Crédito"}
            </Button>
          </div>

          <div className="card-flat p-6 lg:col-span-2">
            <div className="eyebrow mb-3">Resultado</div>
            {!analysis && <div className="text-sm text-slate-400 py-10 text-center">Aguardando análise…</div>}
            {analysis && (
              <div data-testid="wiz-analysis-result">
                <div className="flex items-center justify-between">
                  <div className="font-display text-2xl">{analysis.name}</div>
                  <span className={`status-pill ${STATUS_CLASS[analysis.status]}`}>{STATUS_LABELS[analysis.status]}</span>
                </div>
                <div className="mono text-xs text-slate-500 mt-1">{analysis.cpf}</div>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <Info label="Score" value={analysis.score} />
                  <Info label="Multiplicador" value={`${analysis.multiplier}x`} />
                  <Info label="Limite aprovado" value={fmtBRL(analysis.approved_limit)} />
                </div>
                {analysis.restrictions?.length > 0 && (
                  <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-800">
                    <Warning size={14} className="inline mr-1" />
                    Restrições SPC encontradas: {analysis.restrictions.length}
                  </div>
                )}
                <Button data-testid="wiz-next-1" onClick={goStep2} disabled={analysis.status === "reprovado"} className="btn-primary w-full mt-6 h-11">
                  Próximo <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <>
          <div className="card-flat p-6 mb-4">
            <div className="eyebrow mb-3">Dados do imóvel</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><F label="Endereço do imóvel *"><Input data-testid="wiz-address" value={property.address} onChange={(e) => setProperty({...property, address: e.target.value})} placeholder="Rua, número, complemento, cidade/UF" /></F></div>
              <F label="Aluguel mensal *"><Input data-testid="wiz-property-rent" type="number" value={property.rent} onChange={(e) => setProperty({...property, rent: e.target.value})} /></F>
              <F label="Nome do proprietário"><Input data-testid="wiz-owner" value={property.owner_name} onChange={(e) => setProperty({...property, owner_name: e.target.value})} /></F>
              <F label="Condomínio (mensal)"><Input type="number" value={property.condo} onChange={(e) => setProperty({...property, condo: e.target.value})} /></F>
              <F label="IPTU (mensal)"><Input type="number" value={property.iptu} onChange={(e) => setProperty({...property, iptu: e.target.value})} /></F>
            </div>
          </div>

          {/* Plan comparator */}
          <div className="card-flat p-6" data-testid="wiz-plans">
            <div className="eyebrow mb-1">Escolha o plano</div>
            <div className="font-display text-2xl mb-4">Coberturas lado a lado</div>
            <div className="grid md:grid-cols-3 gap-3">
              {Object.entries(PLAN_INFO).map(([key, info]) => {
                const rent = parseFloat(property.rent || 0);
                const monthly = rent * info.rate;
                const selectedPlan = plan === key;
                return (
                  <button key={key} type="button" onClick={() => setPlan(key)} data-testid={`wiz-plan-${key}`}
                    className={`text-left p-6 rounded-md border-2 transition-all ${selectedPlan ? "border-[var(--emerald)] bg-[var(--emerald-soft)]" : "border-[var(--line)] hover:border-slate-300"}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-display text-2xl">{info.name}</div>
                      {selectedPlan && <span className="status-pill status-ativo">Selecionado</span>}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">Cobertura até <strong>{info.mult}x</strong> o aluguel</div>
                    <div className="metric-value text-3xl mt-4">{fmtBRL(monthly)}<span className="text-xs text-slate-500 font-normal ml-1 mono">/mês</span></div>
                    <div className="text-[11px] text-slate-500 mt-1">Limite total: <span className="mono">{fmtBRL(rent * info.mult)}</span></div>
                    <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                      {info.features.map((f) => <li key={f} className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#059669" /> {f}</li>)}
                    </ul>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="wiz-back-1"><ArrowLeft size={16} className="mr-2" /> Voltar</Button>
            <Button data-testid="wiz-next-2" onClick={goStep3} className="btn-primary h-11 px-6">Próximo <ArrowRight size={16} className="ml-2" /></Button>
          </div>
        </>
      )}

      {/* STEP 3 */}
      {step === 3 && contract && !activation && (
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="card-flat p-8 lg:col-span-3 bg-white" data-testid="wiz-contract-preview">
            <div className="eyebrow mb-1">Minuta do contrato</div>
            <div className="font-display text-2xl mb-4">Pré-visualização</div>
            <div className="text-sm text-slate-700 leading-relaxed border-t border-[var(--line)] pt-4 space-y-3">
              <p className="font-semibold">CONTRATO DE PRESTAÇÃO DE GARANTIA LOCATÍCIA Nº {contract.contract_id.toUpperCase()}</p>
              <p>
                Pelo presente instrumento, de um lado <strong>SAFIMOB GARANTIDORA DE ALUGUEL</strong>, doravante denominada
                GARANTIDORA, e de outro <strong>{contract.tenant_name}</strong>, portador do CPF <span className="mono">{contract.tenant_cpf}</span>,
                doravante denominado LOCATÁRIO, celebram o presente contrato:
              </p>
              <p>
                <strong>Objeto:</strong> A GARANTIDORA se compromete a prestar garantia locatícia ao imóvel situado em{" "}
                <strong>{contract.property_address}</strong>, de propriedade de <strong>{contract.owner_name}</strong>.
              </p>
              <p>
                <strong>Plano contratado:</strong> {PLAN_LABELS[contract.plan]} — Cobertura de até{" "}
                <strong className="text-[var(--emerald)]">{contract.coverage_multiplier}x</strong> o valor mensal do aluguel,
                totalizando <strong className="mono">{fmtBRL(contract.rent_amount * contract.coverage_multiplier)}</strong>.
              </p>
              <p>
                <strong>Valores:</strong> Aluguel <span className="mono">{fmtBRL(contract.rent_amount)}</span> · Condomínio{" "}
                <span className="mono">{fmtBRL(contract.condo_amount)}</span> · IPTU <span className="mono">{fmtBRL(contract.iptu_amount)}</span>{" "}
                · Prêmio mensal <span className="mono">{fmtBRL(contract.monthly_premium)}</span>
              </p>
              <p>
                <strong>Vigência:</strong> {contract.duration_months} meses a contar da data de assinatura e ativação por meio
                do pagamento da primeira parcela.
              </p>
              <p>
                <strong>Coberturas:</strong> aluguel, condomínio, IPTU, danos ao imóvel e demais itens conforme plano contratado.
                A garantia entra em vigor após a assinatura eletrônica e a confirmação de pagamento da parcela de ativação.
              </p>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="card-flat p-6 bg-[var(--emerald)] text-white">
              <div className="eyebrow text-white/70">Resumo</div>
              <div className="font-display text-3xl mt-2">{contract.tenant_name}</div>
              <div className="text-white/70 text-sm mt-1 mono">{contract.tenant_cpf}</div>
              <div className="border-t border-white/20 my-4 pt-4 space-y-2 text-sm">
                <Row label="Plano" value={`${PLAN_LABELS[contract.plan]} (${contract.coverage_multiplier}x)`} />
                <Row label="Aluguel" value={fmtBRL(contract.rent_amount)} />
                <Row label="Prêmio/mês" value={fmtBRL(contract.monthly_premium)} />
                <Row label="Cobertura" value={fmtBRL(contract.rent_amount * contract.coverage_multiplier)} />
                <Row label="Score" value={contract.credit_score || "—"} />
              </div>
            </div>

            <div className="card-flat p-6">
              <div className="eyebrow mb-2">Assinatura eletrônica</div>
              <div className="text-xs text-slate-500 mb-3">Digite o nome completo do responsável pela imobiliária para assinar em nome do inquilino. (Provedor: MOCK — D4Sign/Clicksign quando integrado.)</div>
              <Input data-testid="wiz-signature" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Nome completo" className="font-display italic text-lg" />
              <Button data-testid="wiz-sign" onClick={sign} disabled={signing || !signature.trim()} className="btn-primary w-full mt-4 h-11">
                <PenNib size={16} className="mr-2" /> {signing ? "Processando…" : "Assinar & Ativar"}
              </Button>
            </div>

            <Button variant="outline" onClick={() => setStep(2)} className="w-full" data-testid="wiz-back-2"><ArrowLeft size={16} className="mr-2" /> Voltar</Button>
          </div>
        </div>
      )}

      {/* SUCCESS */}
      {step === 3 && activation && (
        <div className="max-w-2xl mx-auto text-center" data-testid="wiz-success">
          <div className="w-20 h-20 rounded-full bg-[var(--success-soft)] flex items-center justify-center mx-auto">
            <CheckCircle size={44} weight="fill" color="#059669" />
          </div>
          <h2 className="font-display text-4xl tracking-tight mt-6">Contrato ativado!</h2>
          <p className="text-slate-600 mt-3">
            O contrato de <strong>{contract.tenant_name}</strong> foi assinado e a primeira parcela foi gerada{activation.provider === "INTER" ? " no Banco Inter" : ""}.
          </p>

          <div className="card-flat p-6 mt-8 text-left">
            <div className="eyebrow mb-2">1ª parcela — ativação</div>
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-display text-3xl">{fmtBRL(activation.amount)}</div>
                <div className="text-xs text-slate-500 mt-1">Vencimento em 3 dias</div>
              </div>
              <span className="status-pill status-pendente">Pendente</span>
            </div>
            <div className="border-t border-[var(--line)] my-4"></div>
            <InvoicePayment invoice={activation} />
            {activation.provider !== "INTER" && (
              <div className="text-[10px] text-slate-400 mt-3">Cobrança simulada — ative "Cobrança via Banco Inter" em Configuração do Site para emitir boletos reais.</div>
            )}
          </div>

          {contract?.portal_token && (
            <div className="card-flat p-6 mt-4 text-left bg-[var(--emerald-soft)] border-[var(--emerald)]/20" data-testid="wiz-portal-block">
              <div className="flex items-center gap-2 mb-2">
                <LinkIcon size={16} color="#1E2A44" weight="duotone" />
                <div className="eyebrow text-[var(--emerald)]">Portal do inquilino</div>
              </div>
              <div className="text-xs text-slate-600 mb-3">Envie este link para o inquilino acessar contrato, faturas e certificado — sem precisar de login.</div>
              <div className="flex items-center gap-2 bg-white border border-[var(--line)] rounded px-3 py-2">
                <input readOnly value={`${window.location.origin}/portal/${contract.portal_token}`} className="flex-1 text-xs mono bg-transparent outline-none" data-testid="wiz-portal-link" />
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/portal/${contract.portal_token}`); toast.success("Link copiado"); }} className="text-slate-500 hover:text-slate-900" data-testid="wiz-portal-copy"><Copy size={14} /></button>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {contract.tenant_phone && (
                  <a data-testid="wiz-portal-whatsapp"
                    href={`https://wa.me/55${(contract.tenant_phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Olá ${contract.tenant_name}! Sua garantia locatícia Safimob está pronta. Acesse seu portal: ${window.location.origin}/portal/${contract.portal_token}`)}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#25D366] text-white text-xs hover:opacity-90">
                    <WhatsappLogo size={14} weight="fill" /> Enviar por WhatsApp
                  </a>
                )}
                {contract.tenant_email && (
                  <a data-testid="wiz-portal-email"
                    href={`mailto:${contract.tenant_email}?subject=${encodeURIComponent("Sua garantia Safimob — portal do inquilino")}&body=${encodeURIComponent(`Olá ${contract.tenant_name},\n\nSua garantia Safimob foi ativada. Acesse seu portal:\n${window.location.origin}/portal/${contract.portal_token}\n\nSafimob Garantidora de Aluguel`)}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--line)] text-xs hover:bg-white">
                    <EnvelopeSimple size={14} /> Enviar por Email
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8 justify-center">
            <Button variant="outline" onClick={restart} data-testid="wiz-new">Nova contratação</Button>
            <Button className="btn-primary" onClick={() => window.location.href = "/contracts"} data-testid="wiz-go-active"><FileText size={16} className="mr-2" /> Ver Contratações Ativas</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, children }) { return <div><Label className="text-xs text-slate-500 mb-1.5 block">{label}</Label>{children}</div>; }
function Info({ label, value }) {
  return <div className="p-3 rounded-md bg-slate-50 border border-slate-100"><div className="eyebrow">{label}</div><div className="metric-value text-xl mt-1">{value}</div></div>;
}
function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-white/70">{label}</span><span className="mono">{value}</span></div>;
}
